import { useIntl } from 'react-intl';

import { PresetKey } from './types';

const CTA_AI_LAYOUT = {
  border: '1px solid transparent',
  transitionDuration: '300ms',
  transitionTimingFunction: 'ease-out',
  transitionProperty:
    'background, background-color, border, border-color, box-shadow',
  ':hover': {
    borderColor: '#FFFFFF',
    borderStyle: 'solid',
    borderWidth: '1px',
  },
} as const;

const CTA_AI_SELECTED = {
  background: 'none',
  backgroundImage: 'none',
  backgroundColor: 'background',
  border: '1px solid #D1A7F1',
  boxShadow: '0px 0px 8px 0px #9747FF80',
  color: 'text.primary',
  transitionDuration: '300ms',
  transitionTimingFunction: 'ease-out',
  transitionProperty:
    'background, background-color, border, border-color, box-shadow',
} as const;

function ctaAiLayout(
  isSelected: boolean,
  disabled: boolean,
  isLoading: boolean,
) {
  if (isSelected && !disabled && !isLoading) {
    return CTA_AI_SELECTED;
  }
  let base: typeof CTA_AI_LAYOUT | Record<string, unknown>;
  if (disabled || isLoading) {
    base = { ...CTA_AI_LAYOUT } as Record<string, unknown>;
    delete base[':hover'];
  } else {
    base = CTA_AI_LAYOUT;
  }
  return base;
}

export const usePreset = (
  preset?: PresetKey,
  disabled = false,
  isLoading = false,
  isSelected = false,
) => {
  const intl = useIntl();

  if (!preset) {
    return {};
  }

  switch (preset) {
    case 'back':
      return {
        text: intl.formatMessage({
          id: 'Button.backText',
          defaultMessage: 'Back',
          description: 'Text for back button',
        }),
        variant: 'tertiary' as const,
      };
    case 'cancel':
      return {
        text: intl.formatMessage({
          id: 'Button.cancelText',
          defaultMessage: 'Cancel',
          description: 'Text for cancel button',
        }),
        variant: 'tertiary' as const,
      };
    case 'ai':
      return {
        icon: 'ai' as const,
        iconPosition: 'left' as const,
        status: 'ai' as const,
      };
    case 'onboarding':
      return {
        icon: 'help' as const,
        iconPosition: 'left' as const,
        text: intl.formatMessage({
          id: 'Button.onboardingText',
          defaultMessage: 'Onboarding',
          description: 'Text for onboarding button',
        }),
        variant: 'onboarding' as const,
      };
    case 'cta':
      return {
        variant: 'cta' as const,
        size: 'l' as const,
      };
    case 'cta-ai':
      return {
        size: 'l' as const,
        icon: 'evisort-ai-static' as const,
        variant: 'cta-ai' as const,
        layoutStyles: ctaAiLayout(isSelected, disabled, isLoading),
      };
    case 'dropdown':
      return {
        variant: 'secondary' as const,
        iconPosition: 'left' as const,
      };
    default:
      return {};
  }
};
